
import { Observable, from as observableFrom, of as observableOf } from 'rxjs'

import CheckIcon from '@mui/icons-material/Check'
import Button from '@mui/material/Button'
import ConfirmDialog from 'components/ConfirmDialog'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { PPDialog } from 'components/PPDialog'
import PPSwitch from 'components/PPSwitch'
import { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router'
import { catchError, mergeMap } from 'rxjs/operators'
import {
  closeUpgradePrompt,
  featuresSelector,
  featureUpgradeSelector,
  getFeaturesAndLimits,
  getProductByHandle,
  limitsSelector,
  onCloseUpgradeSelector,
  productsSelector,
  productUpgradeSelector,
  toggleContentUpgradePopupDismissed,
  UPGRADE_EVENT_TYPES_MAP,
  upgradeProductV2
} from 'services/product'
import { message } from 'services/snackbar'
import { HS_EVENT_POPUP_UPGRADE, trackHubspotEvent } from 'services/tracking/hubspot'
import { checkSubscription } from 'services/users/actions'
import { currentUserSelector, userIsAdminSelector, userProductSelector } from 'services/users/selectors'
import { bytesToSize, PPFeature, PPLimit, PPProduct, User } from 'shared'
import { CURRENCY_SIGN_MAP, FEATURE_GENERIC, LIMIT_MY_LIBRARY_STORAGE, LIMIT_UNLIMITED } from 'shared/constants'
import StoreState, { StoreThunkDispatch } from 'store/state'
import capitalize from 'utils/format/capitalize'
import styles from './UpgradePopup.pcss'
interface UpgradePopupConnectedProps {
  options: {
    feature?: string
    product?: string
    onClose?: () => void
  }
  user?: User
  features: { [key: string]: PPFeature }
  limits: { [key: string]: PPLimit }
  currentProduct?: PPProduct
  isAdmin: boolean
  closeDialog: () => void
  upgrade: (handle: string) => Observable<any>
  showNotification: (text: string, type?: any, duration?: number) => void
  refreshProductInfo: () => Promise<any>
  dismissContentUpgradePopup: () => void
}

type UpgradePopupProps = UpgradePopupConnectedProps

export function UpgradePopup(props: UpgradePopupProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const navigate = useNavigate()
  const products = useSelector(productsSelector)
  const [loading, setLoading] = useState(false)
  const [isAnnual, setIsAnnual] = useState(true)
  const planUpgradeHandle = props.options.product
  const product = planUpgradeHandle ? dispatch(getProductByHandle(planUpgradeHandle)) : null
  const planUpgradeDisplayName = capitalize(product?.name)
  const feature = props.options.feature || FEATURE_GENERIC
  const upgradeItem = feature !== FEATURE_GENERIC ? props.features[feature] || props.limits[feature] : null
  const currency = props.user?.account.currency as string

  useEffect(() => {
    dispatch(getFeaturesAndLimits()).unwrap()
  }, [dispatch])

  useEffect(() => {
    if (props.options?.feature) {
      ReactGA.event({
        action: 'Impression',
        category: 'Interactions - Pop Up',
        label: upgradeItem?.name,
        value: 0
      })

      const type = UPGRADE_EVENT_TYPES_MAP[props.options.feature] || props.options.feature
      dispatch(trackHubspotEvent(HS_EVENT_POPUP_UPGRADE, { type }))
    }
  }, [dispatch, props.options.feature, upgradeItem?.name])

  const close = () => {
    props.closeDialog()
    if (props.options.onClose) {
      props.options.onClose()
    }
  }

  const onClick = () => {
    ReactGA.event({
      action: 'Click',
      category: 'Interactions - Pop Up',
      label: upgradeItem?.name,
      value: 10
    })
  }

  const onUpgrade = () => {
    if (planUpgradeHandle) {
      const nextProduct = dispatch(getProductByHandle(planUpgradeHandle))
      const hasPaymentInfo = props.user?.account.hasPaymentInfo
      const upgradeHandle = isAnnual ? nextProduct?.handles.annual : nextProduct?.handles.monthly
      if (props.currentProduct?.order === 0 || !hasPaymentInfo) { // redirect Free and Trial users to the Subscription page
        navigate('/settings/subscription', { state: { checkoutProductHandle: upgradeHandle } })
        return
      }

      setLoading(true)

      props.upgrade(upgradeHandle as string).pipe(
        mergeMap(() => observableFrom(props.refreshProductInfo())))
        .pipe(catchError((error) => observableOf({ error })))
        .subscribe((response) => {
          if (response.error) {
            if (response.error.isChargify) {
              props.showNotification(intl.formatMessage(
                { id: 'upgrade-popup.notification-self-service' },
                {
                  a: (...text: any) => (
                    <a href={props.user?.account.selfServicePage} target="_blank">{text}</a>
                  )
                } as any
              ), 'error', NOTIFICATION_DURATION_LONG)
            } else {
              props.showNotification(
                intl.formatMessage({ id: 'notifications.error-w-message' }, { error: response.error.message }) as string,
                'error'
              )
            }
            setLoading(false)
            return
          }
          props.showNotification(intl.formatMessage(
            { id: 'notifications.upgrade-success' },
            { subscription: nextProduct?.name.toUpperCase() }
          ), 'success')
          setLoading(false)
          close()
        })
    }
  }

  const dismiss = () => {
    props.dismissContentUpgradePopup()
    close()
  }

  let currentLimit: any = props.limits[props.options.feature || '']
    ? props.limits[props.options.feature || ''].plans[props.currentProduct?.id || '']
    : null

  if (typeof currentLimit === 'number' && currentLimit !== LIMIT_UNLIMITED && upgradeItem?.key === LIMIT_MY_LIBRARY_STORAGE) {
    const prevProduct = product && products[products.findIndex(p => p.id === product.id) - 1]
    const prevPlanLimit = props.limits[props.options.feature || '']?.plans[prevProduct?.id || '']
    if (prevProduct && typeof prevPlanLimit === 'number') {
      currentLimit = prevPlanLimit === LIMIT_UNLIMITED ? LIMIT_UNLIMITED : bytesToSize(prevPlanLimit)
    } else {
      currentLimit = bytesToSize(currentLimit)
    }
  }

  let title: any = null

  if (upgradeItem) {
    title = (upgradeItem as any)?.isFeature ? `${upgradeItem.name} is available on ${planUpgradeDisplayName} plan and above.`
      : currentLimit === LIMIT_UNLIMITED
        ? `Unlimited ${upgradeItem?.name} is only available with the ${planUpgradeDisplayName} plan and above.`
        : `More than ${currentLimit} ${upgradeItem?.name} is only available with the ${planUpgradeDisplayName} plan and above.`
  }

  const onAnnualChange = (value: 'annual' | 'monthly') => {
    setIsAnnual(value === 'annual')
  }

  const annualPrice = product?.price.annual[currency] ? product.price.annual[currency] / 12 : 0 // eslint-disable-line no-magic-numbers
  const monthlyPrice = product?.price.monthly ? product?.price.monthly[currency] : 0

  return (
    <PPDialog
      open={Boolean(planUpgradeHandle)}
      size="xl"
      data-testid="upgrade-popup"
      onClose={close}
      title={<FormattedMessage id="upgrade-popup.feature-title" values={{ plan: planUpgradeDisplayName }} />}
    >
      <div className={styles.inner} onClick={onClick}>
        <div className={styles.content}>
          <div className={styles['img-box']}>
            <img src={upgradeItem?.image} />
          </div>
          <div className={styles['description-box']}>
            <h3 className={styles.title}>{title}</h3>
            {upgradeItem?.salesPitch && (
              <p className={styles.description} dangerouslySetInnerHTML={{ __html: upgradeItem?.salesPitch }}></p>
            )}
            {upgradeItem?.upgradePopupDismissText && (
              <a className={styles.link} onClick={dismiss}>
                <CheckIcon className={styles.icon} />
                {upgradeItem?.upgradePopupDismissText}
              </a>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles['footer-content']}>
            <div className={styles.left}>
              <div className={styles['left-content']}>
                <p className={styles.name}>{`${planUpgradeDisplayName} plan`}</p>
                <p className={styles.price}>
                  {
                    `${CURRENCY_SIGN_MAP[currency]}${isAnnual
                      ? annualPrice.toFixed(2)
                      : monthlyPrice?.toFixed(2)}/mo`
                  }
                </p>

                <PPSwitch
                  options={[{
                    value: 'monthly',
                    label: 'Monthly'
                  }, {
                    value: 'annual',
                    label: 'Annual'
                  }]}
                  selectedValue={isAnnual ? 'annual' : 'monthly'}
                  className={styles.switch}
                  onSelectedValueChange={onAnnualChange}
                />
              </div>
            </div>
            <div className={styles.right}>
              <ConfirmDialog message={(
                <div className={styles['confirm-msg']}>
                  <p>{`Upgrade to ${planUpgradeDisplayName}${isAnnual ? ' Annual' : ''}?`}</p>
                  <p>
                    <FormattedMessage
                      id="upgrade-popup.confirm-upgrade-hint"
                      values={{
                        a: (...text) => (

                          <a href="https://help.postplanner.com/en/articles/8797516-what-are-the-differences-between-plans" target="_blank">
                            {text}
                          </a>
                        )
                      }}
                    />
                  </p>
                </div>
              )}
              confirmLabel={<FormattedMessage id="actions.upgrade" />}
              >
                {confirm => (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    className={styles['btn-upgrade']}
                    // MUI classes={{ label: styles['btn-label'] }}
                    disabled={loading || !props.isAdmin}
                    onClick={confirm(onUpgrade)}
                  >
                    {upgradeItem?.upgradeActionText || <FormattedMessage id="actions.upgrade" />}
                  </Button>
                )}
              </ConfirmDialog>
              {!props.isAdmin && (
                <p className={styles['disabled-hint']}>
                  <FormattedMessage id="upgrade-popup.member-upgrade-message" />
                </p>
              )}
            </div>
          </div>
          <div className={styles['footer-link']}>
            <Link to="/settings/subscription" onClick={close}>
              See my options for upgrades and add-ons
            </Link>
          </div>
        </div>
      </div>
    </PPDialog>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    options: {
      feature: featureUpgradeSelector(state),
      product: productUpgradeSelector(state),
      onClose: onCloseUpgradeSelector(state)
    },
    currentProduct: userProductSelector(state),
    features: featuresSelector(state),
    limits: limitsSelector(state),
    user: currentUserSelector(state),
    isAdmin: userIsAdminSelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    refreshProductInfo: () => dispatch(checkSubscription())
      .catch((error) => {
        console.log('[checkSubscription()] Error: ', error)
      }),
    closeDialog: () => dispatch(closeUpgradePrompt()),
    upgrade: (handle: string) => dispatch(upgradeProductV2(handle)),
    showNotification: (text: string, type?: any, duration?: number) => dispatch(message(text, type, duration)),
    dismissContentUpgradePopup: () => dispatch(toggleContentUpgradePopupDismissed(true))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UpgradePopup)
